import { ClipboardList } from "lucide-react";
import * as React from "react";

import { Badge } from "@/components/ui/badge";
import { Callout, EmptyState } from "@/components/ui/feedback";
import { ScopePicker, scopeChoices } from "@/features/intake-live/scope-picker";
import { IntakeWizard } from "@/features/intake-live/wizard";
import { ApiError } from "@/lib/api";
import { progressText, walkOf, type Walk } from "@/lib/intake-steps";
import { cn } from "@/lib/utils";
import {
  type IntakeState,
  type IntakeStep,
  useAnswerIntake,
  useLeadIntake,
  useReplanIntake,
  useSkipIntake,
} from "@/lib/queries-intake";
import { useAuth } from "@/lib/auth";
import { useUpdateContact } from "@/lib/queries";
import { type IntakeLead, type StandIn, useIntakeLead } from "@/softphone/intake-lead";
import { seesCampaigns } from "@/lib/types";

/**
 * The intake the rep is working through on this call (ACL-338, ACL-403).
 *
 * One component for both surfaces, as the phone's own panels are: the call screen passes `full`
 * and gets the questions, the dock gets a line saying where the intake stands and sends the rep
 * to the phone page for the rest.
 *
 * **One question at a time** (ACL-403), not the board the admin draws. The whole flow is loaded
 * in the one reading — every step's type, options and stored value — so stepping back through
 * answered questions costs no request. What it will never do is decide what comes *next*: that is
 * the engine's, re-walked from the pinned graph and the lead's current answers on every answer
 * (ADR-110). Two evaluators would eventually disagree, and the rep would be answering a question
 * the flow never chose.
 *
 * **It renders nothing at all unless there is something to show.** No lead on the call, no
 * published flow for the tenant, no active tree: the call screen then looks exactly as it did
 * before this feature existed, which is the same rule the Review card follows (ACL-341).
 */
export function IntakePanel({
  callId,
  full = false,
  standIn,
  whenEmpty,
  onHas,
  className,
  bodyClassName,
}: {
  callId: string;
  full?: boolean;
  /** A lead to borrow when the call names none. Nothing in production passes one. */
  standIn?: StandIn;
  /** What to draw when there is nothing to show. Production draws nothing; see `dev-panel-notes`. */
  whenEmpty?: (why: IntakeLead["why"], hasFlow: boolean) => React.ReactNode;
  /** Whether this call has an intake at all, so the page can offer its switch only then: a pane
   *  button that uncovers nothing reads as broken (`softphone/call-panes.tsx`). */
  onHas?: (has: boolean) => void;
  /** The pane's own class on the work sheet, where it lies across the sheet's two rows as the
   *  form and the script do (`routes/phone.tsx`'s `SHEET_PANE`). */
  className?: string;
  /** For what is under the header only, so the page can rule this body off from the one beside it
   *  while leaving the two headers as one band. */
  bodyClassName?: string;
}) {
  const { leadId, callId: onCall, why, standIn: borrowed } = useIntakeLead(callId, standIn);
  const { data } = useLeadIntake(leadId ?? undefined);
  const answer = useAnswerIntake(leadId ?? "");
  // The same route the contact header edits a firm through, so `set_firm` records who changed it.
  const { user } = useAuth();
  const setFirm = useUpdateContact();
  const replanned = useReplanIntake(leadId ?? "");

  const skip = useSkipIntake(leadId ?? "");
  const walk = React.useMemo(() => walkOf(data), [data]);

  // Told to the page, not read from it: only this component knows whether the lead on the call
  // has a flow to walk.
  const has = Boolean(leadId && data?.flow);
  React.useEffect(() => onHas?.(has), [has, onHas]);

  // Nothing to walk. In the dock's line this says nothing at all, as the Review card does
  // (ACL-341): a tenant with no intake reads exactly as it did before the feature existed.
  //
  // The page's pane says so plainly instead, because its switch is in the rail from the first
  // frame now — offered on the same terms as the form, rather than waiting for this panel to
  // report a flow. A switch that uncovers a blank sheet is the thing that reads as broken, so
  // the pane answers the question a rep just asked by pressing it. `whenEmpty` still wins where
  // it is given: it is the local scaffolding's own notice.
  if (!leadId || !data?.flow) {
    if (!full) return null;
    const said = whenEmpty?.(why, Boolean(data?.flow));
    if (said) return <section aria-label="The intake" className="w-full">{said}</section>;
    return (
      <section aria-label="The intake" className={cn("flex min-h-0 flex-col", className)}>
        <header className="shrink-0 border-b border-border px-4 py-2.5 @2xl/phone:px-6 @4xl/phone:px-8">
          <div className="flex min-h-7 items-center gap-2">
            <h2 className="inline-flex min-w-0 items-center gap-1.5 text-sm font-semibold text-text">
              <ClipboardList className="size-4 shrink-0 text-text-muted" aria-hidden />
              <span className="truncate">Intake</span>
            </h2>
          </div>
        </header>
        <div className={cn("flex min-h-0 flex-1 flex-col overflow-y-auto scrollbar-subtle", bodyClassName)}>
          <EmptyState
            icon={ClipboardList}
            title={why === "no_lead" ? "No contact on this call" : "No questions for this contact"}
            description={
              why === "no_lead"
                ? "The intake is a contact's own, so there is nothing to ask on a call that names none."
                : "Nobody has published an intake for this contact yet."
            }
          />
        </div>
      </section>
    );
  }

  // A refusal from either write, shown by the question rather than as a toast.
  const problem =
    answer.error instanceof ApiError
      ? answer.error.message
      : skip.error instanceof ApiError
        ? skip.error.message
        : null;
  const working = answer.isPending || skip.isPending;

  const submit = (step: IntakeStep, value: unknown) => {
    if (!step.field_key) return;
    answer.mutate({ field_key: step.field_key, value, call_id: onCall });
  };

  if (!full) {
    return (
      <section aria-label="The intake" className="w-full rounded-lg border border-border px-3 py-2">
        <div className="flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-text">
            <ClipboardList className="size-3.5" />
            Intake
          </span>
          <Status walk={walk} />
        </div>
      </section>
    );
  }

  return (
    <section aria-label="The intake" className={cn("flex min-h-0 flex-col", className)}>
      {/* The header the form's and the script's panes have — one row of the same height, the same
          line under it — so the three read as one band where two are side by side. It was a line
          inside the scroll area: the flow's name and where the intake stands scrolled away with
          the questions, and the pane had no header where its neighbour had one. Its gutter is the
          body's, so the name sits over the questions rather than to the left of them. */}
      <header className="shrink-0 border-b border-border px-4 py-2.5 @2xl/phone:px-6 @4xl/phone:px-8">
        <div className="flex min-h-7 items-center gap-2">
          <h2 className="inline-flex min-w-0 items-center gap-1.5 text-sm font-semibold text-text">
            <ClipboardList className="size-4 shrink-0 text-text-muted" aria-hidden />
            <span className="truncate">{data.flow.title}</span>
          </h2>
          <span className="shrink-0 text-xs font-normal text-text-muted">v{data.flow.version}</span>
          {/* At the end of the row, where the form's save state sits. */}
          <div className="ml-auto shrink-0">
            <Status walk={walk} />
          </div>
        </div>
      </header>
      {/* The body: its own scroll, as each pane of the sheet has. The gutter grows with the pane
          (`@container/phone`), because this one is as wide as the page when it is the only pane
          up — and a question card pressed against both edges of a sheet that wide reads as the
          page having no margin at all, which is what it looked like. The card column beside it
          widens its own padding for the same reason. */}
      <div
        className={cn(
          "flex min-h-0 flex-1 flex-col overflow-y-auto scrollbar-subtle",
          "mx-4 p-4 @2xl/phone:px-6 @4xl/phone:px-8",
          bodyClassName,
        )}
      >
        {/* A cap, not a width: below it the list has the pane, and only past it does a row stop
            growing, so the answer never ends up a whole window away from the question it belongs
            to. Wider than the form's own measure beside it, because this list is one column of
            rows where the form is two of fields.

            `max-w-7xl` and not `8xl`: Tailwind's container scale stops at 7, so `max-w-8xl` is a
            utility that does not exist and silently caps nothing at all — the same trap as the
            colour steps this feature already had (`test/intake-tokens.test.ts`). */}
        <div className="flex w-full max-w-7xl flex-col gap-3">
          {/* Only ever drawn when a stand-in lead was supplied, which nothing in production does:
              on a real call the intake is the lead's own and this is unreachable. */}
          {borrowed ? (
            <Callout tone="warning">
              This call names no lead of its own, so the intake below belongs to another contact:{" "}
              <strong>{data.flow.title}</strong> v{data.flow.version} (flow {data.flow.id}, tree{" "}
              {data.flow.tree_id}). A run keeps the version it started on (ADR-110), so a lead
              part-way through an older tree still walks that one.
            </Callout>
          ) : null}

          {data.outdated ? (
            <Callout tone="warning">
              A newer version of this intake has been published. This call keeps the one it started
              on, so the questions do not change under you.
            </Callout>
          ) : null}

          {walk.problem ? (
            <Callout tone="danger">
              {problemText(walk.problem)}
            </Callout>
          ) : null}

          <IntakeWizard
            walk={walk}
            pending={working}
            problem={problem}
            onAnswer={submit}
            onSkip={(step) => skip.mutate(step.key)}
            renderDecision={(key) => (
              <Decided
                data={data}
                nodeKey={key}
                maySet={Boolean(user) && !user?.signed_in_by}
                pending={setFirm.isPending}
                problem={setFirm.error instanceof ApiError ? setFirm.error.message : null}
                onPick={(firmId) =>
                  // The lead's own firm, not an intake answer: the walk then re-reads it and the
                  // route moves off "everyone else" on its own.
                  setFirm.mutate({ id: leadId, firm_id: firmId }, { onSuccess: () => replanned.mutate() })
                }
              />
            )}
          />
        </div>
      </div>
    </section>
  );
}

/**
 * What one split settled, and the picker when that is the missing fact (ACL-339, ACL-340).
 *
 * Drawn inside the step's own row in the list, at the point the walk passed it. That placement is
 * the whole value: a firm split decides which questions come *after* it, so a rep who answers
 * three questions and then reads "it took everyone else" at the bottom of the screen has the
 * explanation after the thing explained.
 *
 * A `Vary by firm or campaign` step reads the lead — the firm from their own tags, the campaign
 * from the call — so there is nothing to answer here and no dropdown would help: picking one
 * would quietly rewrite whose client this is, which is the CRM's to say, not the intake's. What a
 * rep needs is the reason, and the one case where they can act: a lead with no firm at all, which
 * matched no firm branch and fell to the Fallback.
 */
function Decided({
  data, nodeKey, maySet, pending, problem, onPick,
}: {
  data: IntakeState;
  nodeKey: string;
  maySet: boolean;
  pending: boolean;
  problem: string | null;
  onPick: (firmId: number) => void;
}) {
  const walked = React.useMemo(
    () => new Set((data.taken ?? []).map((one) => `${one.from_key}:${one.position}`)),
    [data.taken],
  );
  const why = explainSplit(data, walked, nodeKey, !seesCampaigns(useAuth().user?.role));
  const choices = scopeChoices(data.edges, nodeKey);
  // A firm split with firms to choose from is editable whether or not one is recorded already.
  // It used to be offered only when the firm was null AND the walk had fallen to the Fallback,
  // which meant the common case — a firm already set, possibly the wrong one — had no control at
  // all: a rep told "it went by firm: TSK Law" on a call about a different firm could read the
  // mistake and not correct it. `ScopePicker` was built for this: it shows the firm as `current`,
  // says "Change the firm", and refuses a press that would set what is already set.
  //
  // A campaign split stays read-only, and that is not an oversight: a campaign is the line the
  // lead was called on, so there is nothing a rep could correctly change (`ScopePicker` says so
  // in its own words).
  const settable = choices?.kind === "firm" && choices.options.length > 0;
  // Only one of the firms the split actually branches on can be shown as the current choice; a
  // lead whose firm has no path of its own is a true "everyone else" and the select would
  // otherwise read as though an option were missing.
  const current =
    data.scope.firm_id !== null &&
    choices?.options.some((one) => one.id === String(data.scope.firm_id))
      ? String(data.scope.firm_id)
      : null;

  if (!why && !settable) return null;
  return (
    <>
      {why ? <p className="mt-0.5 text-xs text-text-muted">{why}</p> : null}
      {settable && choices ? (
        <div className="mt-1.5">
          <ScopePicker
            kind={choices.kind}
            options={choices.options}
            current={current}
            maySet={maySet}
            pending={pending}
            problem={problem}
            onPick={(id) => onPick(Number(id))}
          />
        </div>
      ) : null}
    </>
  );
}

/**
 * Why a split went the way it did, for a step nobody answers (ACL-339, ACL-340).
 *
 * A `Vary by firm or campaign` step reads the lead — the firm from their own tags, the campaign
 * from the call — so a rep has nothing to pick and no dropdown would help: picking one here would
 * quietly rewrite whose client this is, which is the CRM's to say, not the intake's.
 *
 * What a rep needs is the reason. A lead with no firm matches no firm branch, takes the Fallback,
 * and the card said nothing at all — so "I filled the answer and it went to everyone else" read
 * as the flow ignoring the work. This says which fact decided it and which one is missing.
 */
function explainSplit(
  state: IntakeState,
  walked: ReadonlySet<string>,
  key: string,
  asRep = false,
): string | null {
  const out = state.edges.filter((one) => one.from_key === key);
  if (out.length < 2) return null;

  const sources = new Set(
    out.flatMap((one) => one.conditions.map((c) => c.source)).filter((s) => s === "firm" || s === "campaign"),
  );
  if (sources.size === 0) return null;

  const came = out.find((one) => walked.has(`${key}:${one.position}`)) ?? null;
  const onFallback = came !== null && came.conditions.length === 0;
  const byFirm = sources.has("firm");
  // A rep reads no campaign (ACL-652), and cannot change it anyway.
  if (asRep && !byFirm) return null;
  const what = byFirm ? "firm" : "campaign";
  const have = byFirm ? state.scope.firm_label : state.scope.campaign_name;

  if (!came) {
    return have
      ? `Goes by ${what}: this lead's is ${have}.`
      : `Goes by ${what}, and this lead has none set.`;
  }
  if (!onFallback) {
    return `Went by ${what}: ${have ?? "the one on this lead"}.`;
  }
  return have
    ? `This lead's ${what} (${have}) is not one of the paths, so it took "everyone else".`
    : `No ${what} on this lead, so it took "everyone else".`;
}

/** Where the intake stands, in a few words. Never a percentage: the route is the progress. */
function Status({ walk }: { walk: Walk }) {
  if (walk.settled) return <Badge tone="success">Intake complete</Badge>;
  if (walk.problem) return <Badge tone="danger">Needs an admin</Badge>;
  if (walk.current) {
    return (
      <span className="truncate text-xs text-text-muted" title={walk.current.step.title}>
        {progressText(walk)}
      </span>
    );
  }
  return <span className="text-xs text-text-muted">Not started</span>;
}

function problemText(problem: string): string {
  switch (problem) {
    case "no_start":
      return "This intake has no first question yet, so there is nothing to ask.";
    case "dangling":
      return "A step in this intake leads somewhere that no longer exists. An admin needs to fix the flow.";
    case "cycle":
      return "This intake loops back on itself, so it cannot be walked. An admin needs to fix the flow.";
    case "no_branch":
      return "No path out of this step matches this lead, so the intake stops here. The flow needs a path for everyone else.";
    default:
      return "Something is wrong with this intake's setup. An admin needs to look at the flow.";
  }
}
